// @ts-nocheck
// TODO: tighten types after migration
import React, { useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { API_ENDPOINTS } from '../../config/api';
import SubscriptionPaywall from "../../components/SubscriptionPaywall";
import { getAuthToken } from '../../utils/auth';
import './Typewriting.css';

function Typewriting() {
    const [showPaywall, setShowPaywall] = React.useState(false);
    const iframeRef = useRef(null);

    const getToken = () => {
        return getAuthToken();
    };

    const sendToIframe = useCallback((data) => {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(data, '*');
        }
    }, []);

    // Theme sync
    const sendTheme = useCallback(() => {
        const isDark = document.body.classList.contains('dark-mode');
        sendToIframe({ type: 'theme-change', dark: isDark });
    }, [sendToIframe]);

    useEffect(() => {
        const observer = new MutationObserver(() => sendTheme());
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });
        return () => observer.disconnect();
    }, [sendTheme]);

    // Fetch stats and progress from backend and send to iframe
    const loadUserData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            sendToIframe({ type: 'user-data', stats: [], progress: [], isSubscribed: false });
            return;
        }

        try {
            const headers = { 'Authorization': 'Bearer ' + token };
            const [statsRes, progressRes] = await Promise.all([
                fetch(API_ENDPOINTS.TYPEWRITING.STATS, { headers }),
                fetch(API_ENDPOINTS.TYPEWRITING.PROGRESS, { headers }),
            ]);

            const statsText = await statsRes.text();
            const progressText = await progressRes.text();

            let stats = [];
            let progress = [];

            try {
                const trimmedStats = statsText ? statsText.trim() : '';
                if (trimmedStats && trimmedStats !== 'undefined' && trimmedStats !== 'null') {
                    stats = JSON.parse(trimmedStats);
                }
            } catch (e) { /* ignore */ }

            try {
                const trimmedProgress = progressText ? progressText.trim() : '';
                if (trimmedProgress && trimmedProgress !== 'undefined' && trimmedProgress !== 'null') {
                    progress = JSON.parse(trimmedProgress);
                }
            } catch (e) { /* ignore */ }

            // Check actual subscription status (not the static user profile field)
            let isSubscribed = false;
            try {
                const subRes = await fetch(API_ENDPOINTS.SUBSCRIPTION.STATUS, { headers });
                const subText = await subRes.text();
                const trimmedSub = subText ? subText.trim() : '';
                if (trimmedSub && trimmedSub !== 'undefined' && trimmedSub !== 'null') {
                    const subData = JSON.parse(trimmedSub);
                    if (subData && subData.active) {
                        isSubscribed = true;
                    }
                }
            } catch (e) { /* ignore */ }

            sendToIframe({ type: 'user-data', stats: stats, progress: progress, isSubscribed: isSubscribed });
        } catch (err) {
            console.error('Failed to load typewriting data:', err);
            sendToIframe({ type: 'user-data', stats: [], progress: [], isSubscribed: false });
        }
    }, [sendToIframe]);

    // Listen for messages from iframe
    useEffect(() => {
        const handleMessage = async (event) => {
            const data = event.data;
            if (!data || !data.type) return;

            if (data.type === 'typewriting-limit-exceeded') {
                setShowPaywall(true);
                return;
            }

            const token = getToken();
            if (!token) return;

            const headers = {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json',
            };

            if (data.type === 'save-stats') {
                try {
                    const rawText = await fetch(API_ENDPOINTS.TYPEWRITING.STATS, {
                        method: 'POST',
                        headers: headers,
                        body: JSON.stringify(data.payload),
                    }).then(r => r.text());

                    const trimmed = rawText ? rawText.trim() : '';
                    if (trimmed && trimmed !== 'undefined' && trimmed !== 'null') {
                        const saved = JSON.parse(trimmed);
                        sendToIframe({ type: 'stats-saved', stat: saved });
                    }
                } catch (err) {
                    console.error('Failed to save stats:', err);
                }
            }

            if (data.type === 'save-progress') {
                try {
                    await fetch(API_ENDPOINTS.TYPEWRITING.PROGRESS, {
                        method: 'POST',
                        headers: headers,
                        body: JSON.stringify(data.payload),
                    });
                } catch (err) {
                    console.error('Failed to save progress:', err);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [sendToIframe]);

    const handleIframeLoad = () => {
        sendTheme();
        loadUserData();
    };

    return (
        <>
            <Helmet>
                <title>Typewriting – Practice Typing | Hari Vishnu</title>
                <meta name="description" content="Practice and improve your typing speed with this free typing test app. Track WPM, accuracy, and progress with guided lessons." />
                <meta name="robots" content="index,follow" />
                <link rel="canonical" href="https://apps.harivishnu.in/typewriting" />
            </Helmet>

            <div className="typewriting-wrapper mx-auto my-0 w-[min(100%,1400px)] overflow-hidden rounded-[14px] border border-slider bg-bg">
                <iframe
                    ref={iframeRef}
                    src="/typewriting/"
                    title="Typewriting - Typing Practice"
                    className="typewriting-iframe h-full w-full border-none"
                    allow="clipboard-read; clipboard-write"
                    onLoad={handleIframeLoad}
                />
            </div>

            {showPaywall && (
                <div className="lw-paywall-modal-overlay fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-[16px]" onClick={() => setShowPaywall(false)}>
                    <div className="lw-paywall-modal-container relative w-full max-w-[680px] rounded-[14px] border border-slider bg-bg p-[12px]" onClick={e => e.stopPropagation()}>
                        <button className="lw-paywall-close absolute right-[8px] top-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-full border-none bg-transparent text-[#666] hover:bg-black/5" onClick={() => setShowPaywall(false)} aria-label="Close">
                            <i className="fi fi-rr-cross-small"></i>
                        </button>
                        <SubscriptionPaywall>
                             <div className="p-4 text-center">
                                 <h3 className="text-xl font-bold mb-2">Free Limit Exceeded</h3>
                                 <p className="text-muted-foreground mb-4">You've reached the free limit for lessons. Subscribe to unlock all languages and chapters!</p>
                             </div>
                        </SubscriptionPaywall>
                    </div>
                </div>
            )}
        </>
    );
}

export default Typewriting;
